import { CheckIcon } from "@heroicons/react/24/outline";

/**
 * The concierge's working indicator.
 *
 * Extracted from StepConcierge so the guided demo can show the *same* one
 * while it replays a recorded plan. A lookalike would drift the first time
 * either was restyled, and the demo's whole claim is that it shows the real
 * product — a loader that isn't the real loader undercuts that for no reason.
 */

/** The real stages behind the wait — shown so it reads as work, not a spinner. */
export const CONCIERGE_STAGES = [
  "Reading your profile",
  "Matching the Newsquawk library",
  "Composing your dashboard",
] as const;

const BAR_HEIGHTS = [10, 18, 28, 20, 34, 22, 14, 26, 12];

/** Squawk waveform — the audio motif doubles as the working indicator. */
export function Waveform() {
  return (
    <div className="flex h-9 items-center gap-[3px]" aria-hidden>
      {BAR_HEIGHTS.map((height, index) => (
        <span
          key={index}
          className="nq-onb-bar w-[3px] rounded-full bg-squawk-blue"
          style={{ height, ["--nq-i" as string]: index }}
        />
      ))}
    </div>
  );
}

/** Waveform + the stage checklist. `stage` is the index currently running. */
export default function ConciergeWorking({ stage }: { stage: number }) {
  return (
    <div className="py-6">
      <Waveform />
      <div className="nq-onb-sweep relative mt-5 h-px w-full overflow-hidden bg-gray-700" />
      <ul className="mt-6 space-y-2.5">
        {CONCIERGE_STAGES.map((label, index) => (
          <li
            key={label}
            className={`flex items-center gap-2.5 text-sm transition-colors duration-500 ${
              index <= stage ? "text-gray-200" : "text-gray-600"
            }`}
          >
            <span
              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors duration-500 ${
                index < stage
                  ? "border-squawk-blue bg-squawk-blue text-white"
                  : index === stage
                    ? "border-squawk-blue text-squawk-blue"
                    : "border-white/10"
              }`}
            >
              {index < stage && (
                <CheckIcon className="h-2.5 w-2.5" strokeWidth={3} />
              )}
              {index === stage && (
                <span className="h-1.5 w-1.5 rounded-full bg-squawk-blue" />
              )}
            </span>
            {label}
          </li>
        ))}
      </ul>
    </div>
  );
}
